'use client';

import RichText from '@app/_blocks/RichText';
import { Heading } from '@components/ui/Heading';
import { Paragraph } from '@components/ui/Paragraph';
import { useSiteInfo } from '@context/siteInfoContext';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@ngg/icons';
import type { ProductPageInfoBlockFragment } from '@server/gql/graphql';
import { Fragment, useEffect, useState } from 'react';
import type { Variant } from 'types';

import type { MarketConfiguration } from '@/config/markets/const';
import { cn } from '@/lib/utils';
import { getGooglePriceForVariant } from '@/utils/googlePricing';
import { formatPrice } from '@/utils/priceUtils';

type PriceProps = {
    from?: string;
    price: number;
    marketConfig: MarketConfiguration;
    className?: string;
    priceType?: string;
    originalPrice?: number;
    recommendedPrice?: number;
    priceInfoBlock?: ProductPageInfoBlockFragment;
    variant?: Variant;
};

export default function Price({
    from,
    price,
    marketConfig,
    className,
    priceType,
    recommendedPrice,
    originalPrice = price,
    priceInfoBlock,
    variant,
}: PriceProps) {
    const [tooltip, setTooltip] = useState(false);
    const { dictionary: globalDictionary } = useSiteInfo();
    const [GADPrice, setGADPrice] = useState<number>();

    useEffect(() => {
        const personalPrice = getGooglePriceForVariant(variant?.id);
        if (personalPrice) setGADPrice(personalPrice);
        return () => setGADPrice(undefined);
    }, [variant?.id]);

    if (GADPrice) {
        return (
            <div className="flex flex-col">
                <Paragraph className="font-medium lg:text-lg">
                    {formatPrice(GADPrice, marketConfig.locale)}
                    <span className="ml-2 font-medium text-grey-300 line-through lg:text-lg">
                        {formatPrice(
                            recommendedPrice ?? originalPrice,
                            marketConfig.locale,
                        )}
                    </span>
                </Paragraph>
            </div>
        );
    }

    return (
        <div
            className={cn(
                'relative flex flex-col flex-wrap gap-4 lg:gap-6',
                className,
            )}>
            {priceType === 'standard' && (
                <div className="flex flex-col">
                    <Paragraph className="font-medium lg:text-lg">
                        {from} {formatPrice(price, marketConfig.locale)}
                    </Paragraph>
                </div>
            )}
            {priceType === 'priceMatch' && (
                <div className="flex flex-col">
                    <Paragraph className="font-medium lg:text-lg">
                        {from} {formatPrice(price, marketConfig.locale)}
                    </Paragraph>
                    <Paragraph className="text-xs text-grey-300">
                        {globalDictionary.recPrice}:{' '}
                        {formatPrice(originalPrice, marketConfig.locale)}
                        {priceInfoBlock && (
                            <button
                                onClick={() => setTooltip(true)}
                                className="ml-2 text-grey-300 underline">
                                {globalDictionary.readMore}
                            </button>
                        )}
                    </Paragraph>
                </div>
            )}
            {priceType === 'campaign' && (
                <div className="flex flex-col">
                    <Paragraph className="font-medium lg:text-lg">
                        {from} {formatPrice(price, marketConfig.locale)}
                        {recommendedPrice && (
                            <span className="ml-2 font-medium text-grey-300 line-through lg:text-lg">
                                {formatPrice(
                                    recommendedPrice,
                                    marketConfig.locale,
                                )}
                            </span>
                        )}
                    </Paragraph>
                    {originalPrice &&
                        recommendedPrice &&
                        originalPrice < recommendedPrice && (
                            <Paragraph className="col-span-2 text-xs text-grey-300">
                                {globalDictionary.previousPrice}:{' '}
                                {formatPrice(
                                    originalPrice,
                                    marketConfig.locale,
                                )}
                                {priceInfoBlock && (
                                    <button
                                        onClick={() => setTooltip(true)}
                                        className="ml-2 text-grey-300 underline">
                                        {globalDictionary.readMore}
                                    </button>
                                )}
                            </Paragraph>
                        )}
                </div>
            )}
            {priceType === 'PV2' && (
                <div className="flex flex-col">
                    <Paragraph className="font-medium lg:text-lg">
                        {formatPrice(price, marketConfig.locale)}
                        <span className="ml-2 font-medium text-grey-300 line-through lg:text-lg">
                            {formatPrice(
                                recommendedPrice ?? originalPrice,
                                marketConfig.locale,
                            )}
                        </span>
                    </Paragraph>
                </div>
            )}
            <Transition appear show={tooltip} as={Fragment}>
                <Dialog
                    open={tooltip}
                    onClose={() => setTooltip(false)}
                    className="relative z-40">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <div
                            className="fixed inset-0 bg-black/30"
                            aria-hidden="true"
                        />
                    </Transition.Child>
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="relative mx-auto max-h-full max-w-xl items-start overflow-auto bg-white px-8 py-8 text-left prose-hr:my-6 md:py-12">
                                <button
                                    type="button"
                                    onClick={() => setTooltip(false)}
                                    className="absolute right-0 top-2 p-4">
                                    <XIcon className="text-base" />
                                </button>
                                <Dialog.Title
                                    as={Heading}
                                    size={4}
                                    className="mb-4">
                                    {priceInfoBlock?.title}
                                </Dialog.Title>
                                <RichText
                                    content={priceInfoBlock?.content}
                                    market={marketConfig.code}
                                    locale={marketConfig.locale}
                                />
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
}
